import React, { useMemo, useEffect } from 'react';
import { Button, Select, SelectItem, Tooltip } from '@heroui/react';
import { useScheduler } from '../SchedulerContext';
import {
  RequiredInfo,
  RequiredInfoValue,
  Service,
  EquipmentQuantity,
  Bundle,
} from '@/util/types/serverTypes';
import RequiredInfoAccordion from '@/components/scheduling/RequiredInfoAccordion';
import ClosingDateField from '@/components/scheduling/ClosingDateField';
import EquipmentSection from '@/components/scheduling/EquipmentSection';
import { checkEscapeHatch } from '../utils/checkEscapeHatch';
import { useRequiredInfoValidation } from '../utils/useRequiredInfoValidation';
import { RiInformationLine } from 'react-icons/ri';
import NextLink from 'next/link';

interface PropertyMoreInfoProps {
  allRequiredInfo: RequiredInfo[];
  allServices: Service[];
  bundles: Bundle[];
  onContinue: () => void;
}

export default function PropertyMoreInfo({
  allRequiredInfo,
  allServices,
  bundles,
  onContinue,
}: PropertyMoreInfoProps) {
  const {
    selectedPrimaryService,
    selectedAddons,
    selectedBundle,
    selectedServiceIds,
    requiredInfoValues,
    setRequiredInfoValues,
    equipmentQuantities,
    setEquipmentQuantities,
    address,
    containingServiceAreaArray,
    scheduleSettings,
    isLoadingDraft,
    companyStyles,
    setEscapeHatchReason,
    referralSource,
    setReferralSource,
    closingDate,
    setClosingDate,
    markPropertyPageSeen,
  } = useScheduler();

  /**
   * Being shown this page is half of what releases the Property step's Next
   * button. Only the active internal page is rendered, so mounting *is* the
   * customer arriving here.
   */
  useEffect(() => {
    markPropertyPageSeen('moreInfo');
  }, [markPropertyPageSeen]);

  // Log when draft data is loaded (for debugging)
  useEffect(() => {
    if (
      !isLoadingDraft &&
      (requiredInfoValues.length > 0 || equipmentQuantities.length > 0)
    ) {
      console.log('[Draft Hydration] PropertyMoreInfo loaded with:', {
        requiredInfoValues: requiredInfoValues.length,
        equipmentQuantities: equipmentQuantities.length,
      });
    }
  }, [requiredInfoValues, equipmentQuantities, isLoadingDraft]);

  // Handle continue - check for escape hatches before proceeding
  const handleContinue = () => {
    // Check if booking should be blocked due to escape hatch scenarios
    const escapeHatchReason = checkEscapeHatch({
      selectedServiceIds,
      services: allServices,
      containingServiceAreaArray,
      address,
      maxSqftOnline: scheduleSettings.maxSqftOnline,
    });

    if (escapeHatchReason) {
      // Set the escape hatch reason to trigger the escape hatch page
      setEscapeHatchReason(escapeHatchReason);
      // Still call onContinue to navigate to the next step (schedule)
      // The SchedulerContent will render the escape hatch page instead of the slots step
      onContinue();
      return;
    }

    // No blocking conditions - proceed normally
    onContinue();
  };

  // Get selected services (including from bundles)
  const selectedServices = useMemo(() => {
    const services: Service[] = [];
    const serviceIdsSet = new Set<string>();

    // Add primary service
    if (selectedPrimaryService) {
      serviceIdsSet.add(selectedPrimaryService);
      const primary = allServices.find((s) => s._id === selectedPrimaryService);
      if (primary) services.push(primary);
    }

    // Add addon services
    selectedAddons.forEach((addonId) => {
      if (!serviceIdsSet.has(addonId)) {
        serviceIdsSet.add(addonId);
        const addon = allServices.find((s) => s._id === addonId);
        if (addon) services.push(addon);
      }
    });

    // Add services from selected bundle
    if (selectedBundle) {
      const bundle = bundles.find((b) => b._id === selectedBundle);
      if (bundle && bundle.items) {
        bundle.items.forEach((item) => {
          const serviceId = item._serviceId;
          // Only add if not already included (avoid duplicates)
          if (serviceId && !serviceIdsSet.has(serviceId)) {
            serviceIdsSet.add(serviceId);
            const service = allServices.find((s) => s._id === serviceId);
            if (service) services.push(service);
          }
        });
      }
    }

    return services;
  }, [
    selectedPrimaryService,
    selectedAddons,
    selectedBundle,
    bundles,
    allServices,
  ]);

  // Which questions apply, and which are still unanswered. This is the same rule
  // the step validators use, so this button and the stepper can never disagree
  // about whether the customer may move on.
  const { requiredInfoIds, isValid } = useRequiredInfoValidation();

  // Check if services have any equipment requirements
  const hasEquipment = useMemo(() => {
    return selectedServices.some(
      (service) =>
        service.equipmentRequired && service.equipmentRequired.length > 0
    );
  }, [selectedServices]);

  if (requiredInfoIds.length === 0 && !hasEquipment) {
    return (
      <div className='flex min-h-[400px] flex-col items-center justify-center gap-4'>
        <p className='text-center text-lg text-gray-600'>
          No additional information needed for your selected services.
        </p>
        <Button
          size='lg'
          onPress={onContinue}
          className={companyStyles.secondaryButtonClassName}
          style={companyStyles.secondaryButtonStyles}
        >
          Continue to Scheduling
        </Button>
      </div>
    );
  }

  return (
    <div className='mx-auto w-full max-w-4xl space-y-6'>
      {/* Header */}
      <div className='text-center'>
        <h2 className='text-2xl font-bold text-gray-800'>
          Just a Few More Questions
        </h2>
        <p className='mt-2 text-gray-600'>
          Fill out what you can - this helps us prepare for your inspection
        </p>
      </div>

      {/* Required Info Section */}
      {requiredInfoIds.length > 0 && (
        <div className='w-full'>
          <RequiredInfoAccordion
            allRequiredInfo={allRequiredInfo}
            requiredInfoIds={requiredInfoIds}
            values={requiredInfoValues}
            setValues={setRequiredInfoValues}
          />
        </div>
      )}

      {/* Closing Date — optional; a buyer under contract usually knows it */}
      <div className='w-full rounded-lg border bg-gray-100 p-4'>
        <ClosingDateField
          value={closingDate}
          onChange={setClosingDate}
          description='Optional — skip this if you do not have a closing date yet'
          className='w-full'
        />
      </div>

      {/* Referral Source Section */}
      {/* REMOVING FOR NOW UNTIL WE GET ALIGNMENT */}
      {/* ✅ TODO: Decide if we want to add this back in */}
      {/* {scheduleSettings?.referralSourceOptions &&
        scheduleSettings.referralSourceOptions.length > 0 && (
          <div className='w-full rounded-lg border bg-gray-100 p-4'>
            <div className='flex items-center gap-2'>
              <Select
                label='Referral Source'
                placeholder='Select source (optional)'
                selectedKeys={referralSource ? [referralSource] : []}
                onChange={(e) => setReferralSource(e.target.value)}
                size='sm'
                variant='bordered'
                classNames={{
                  trigger: 'border bg-white shadow-none',
                  label: 'text-sm font-semibold',
                }}
              >
                {scheduleSettings.referralSourceOptions.map((option) => (
                  <SelectItem key={option}>{option}</SelectItem>
                ))}
              </Select>
            </div>
            <p className='mt-2 text-xs text-gray-500'>
              Let us know where you heard about us!
            </p>
          </div>
        )} */}

      {/* Equipment Section */}
      {/* <EquipmentSection
        selectedServices={selectedServices}
        equipmentQuantities={equipmentQuantities}
        setEquipmentQuantities={setEquipmentQuantities}
      /> */}

      {/* Continue Button */}
      <div className='flex justify-center gap-3 pt-4'>
        <Button
          size='lg'
          onPress={handleContinue}
          isDisabled={!isValid}
          className={companyStyles.secondaryButtonClassName}
          style={companyStyles.secondaryButtonStyles}
        >
          Continue to Scheduling
        </Button>
        {isValid && (
          <Button
            size='lg'
            onPress={handleContinue}
            variant='flat'
            color='default'
          >
            Skip
          </Button>
        )}
      </div>

      {/* Validation message */}
      {!isValid && (
        <p className='text-center text-sm text-danger'>
          Please fill out all required fields to continue
        </p>
      )}
    </div>
  );
}
